import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { merge, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil, filter } from 'rxjs/operators';
import { TreoMediaWatcherService } from 'tsui/@treo';
import { ReportLayout } from 'app/modules/reports/reports.types';
import { ReportsService } from 'app/modules/reports/reports.service';
import { ReportAddComponent } from 'app/modules/reports/add/add.component';
import { ContactSendMessageComponent } from 'app/modules/contacts/modals/send-message/send-message.component';
import orderBy from 'lodash-es/orderBy';
import { generateDownloadableFile } from 'app/shared/functions';
import { FileFormat } from 'app/shared/enums';
import { NewPermission } from '@core/enums';

@Component({
  selector: 'reports-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsListComponent implements OnInit, OnDestroy {
  reportLayouts$: Observable<ReportLayout<any>[]>;
  reportLayouts: ReportLayout<any>[];
  reportsCount: number;
  reportsTableColumns: string[];

  drawerMode: 'side' | 'over';
  searchInputControl: UntypedFormControl;
  selectedContact: ReportLayout<any>;
  isLoading: boolean = false;
  protected readonly NewPermission = NewPermission;

  @ViewChild('matDrawer', { static: true })
  matDrawer: MatDrawer;

  @ViewChild(MatPaginator)
  private _paginator: MatPaginator;

  @ViewChild(MatSort)
  private _sort: MatSort;

  // Private
  private _unsubscribeAll: Subject<void>;

  /**
   * Constructor
   *
   * @param {ActivatedRoute} _activatedRoute
   * @param {TreoMediaWatcherService} _treoMediaWatcherService
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {ContactsService} _contactsService
   * @param {DOCUMENT} _document
   * @param {Router} _router
   * @param {MatDialog} _matDialog
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _treoMediaWatcherService: TreoMediaWatcherService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _reportsService: ReportsService,
    @Inject(DOCUMENT) private _document: any,
    private _router: Router,
    private _matDialog: MatDialog,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Set the defaults
    this.reportsCount = 0;
    this.reportsTableColumns = ['name', 'created_at', 'actions'];
    this.searchInputControl = new UntypedFormControl();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get the contacts
    this.reportLayouts$ = this._reportsService.reportLayouts$;
    this._reportsService.reportLayouts$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((reportLayouts: ReportLayout<any>[]) => {
        // Update the counts
        this.reportsCount = reportLayouts.length;
        if (reportLayouts) {
          this.reportLayouts = orderBy(reportLayouts, ['name'], ['asc']);
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  openContactSendMessageModal(contact): void {
    const dialogRef = this._matDialog.open(ContactSendMessageComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { contact: contact },
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  /**
   * Go to contact
   *
   * @param id
   */
  goToContact(id: string): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Go to contact
    this._router.navigate(['/contacts/', id]);
    //this._router.navigate(['../', id], {relativeTo: route});

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * On backdrop clicked
   */
  onBackdropClicked(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Go to the parent route
    this._router.navigate(['../'], { relativeTo: route });

    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Create contact
   */
  createReportLayout(): void {
    // Create the contact
    /*this._contactsService.createContact({first_name: 'New', last_name: 'Contact'}).subscribe((newContact) => {

            // Go to new contact
            this.goToContact(newContact.id);
        });*/
    // Open the dialog
    const dialogRef = this._matDialog.open(ReportAddComponent, {
      panelClass: 'mailbox-compose-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  runReport(reportLayout: ReportLayout<any>, format: string = 'pdf'): void {
    reportLayout.running = true;
    this._changeDetectorRef.markForCheck();
    let parsedDefinition = JSON.parse(reportLayout.definition);
    let parsedFilters = JSON.parse(reportLayout.filters);
    this._reportsService
      .runReport(reportLayout.id, parsedDefinition.pipeline.fields, parsedFilters)
      .subscribe((data) => {
        reportLayout.running = false;
        if (data) generateDownloadableFile(data, FileFormat.PDF, `${reportLayout.name}`);
      });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  sortChanged(sort: Sort): void {
    const direction = sort.direction === 'asc' ? 'asc' : 'desc';
    this.reportLayouts = orderBy(this.reportLayouts, [sort.active], [direction]);
  }
}
